import { useId, type ReactElement, cloneElement } from 'react';
import { cn } from '../foundation/cn';
import { Label } from './Label';

export interface FormFieldProps {
  readonly label?: string;
  readonly required?: boolean;
  readonly hint?: string;
  readonly error?: string;
  readonly className?: string;
  // A single form control; it receives id/aria wiring automatically.
  readonly children: ReactElement<{ id?: string; 'aria-describedby'?: string; invalid?: boolean }>;
}

// Composition wrapper: pairs a Label + control + hint/error and wires accessible ids.
export function FormField({
  label,
  required = false,
  hint,
  error,
  className,
  children,
}: FormFieldProps): JSX.Element {
  const id = useId();
  const controlId = children.props.id ?? id;
  const describedById = `${id}-desc`;
  const hasMessage = error !== undefined || hint !== undefined;

  const override: { id: string; invalid?: boolean; 'aria-describedby'?: string } = {
    id: controlId,
  };
  if (error !== undefined) {
    override.invalid = true;
  }
  if (hasMessage) {
    override['aria-describedby'] = describedById;
  }
  const control = cloneElement(children, override);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label !== undefined && (
        <Label htmlFor={controlId} required={required}>
          {label}
        </Label>
      )}
      {control}
      {hasMessage && (
        <p
          id={describedById}
          role={error !== undefined ? 'alert' : undefined}
          className={cn('text-xs', error !== undefined ? 'text-danger' : 'text-fg-muted')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
